import { colors, HStack, Link, List, ListItem, Text } from "@biblioteksentralen/react";
import {
  DateHelper,
  eventTimeString,
  getDateStringFormat,
  getPath,
  isRepeatedEventOccurence,
  isThisYear,
  ResolvedEvent,
  ResolvedRepeatedEventOccurrenceSummary,
} from "@libry-content/common";
import { FrontendLocale } from "@libry-content/localization";
import { ReactNode, useState } from "react";
import { Plus } from "react-feather";
import { localizedFormat } from "../../utils/date";
import { useTranslation } from "../../utils/hooks/useTranslation";
import InternalLink from "../InternalLink";
import { useEventStatusLabel } from "./EventStatus";
import { Meta } from "./Meta";

const formatFutureDate = (languageCode: FrontendLocale, date: string): string => {
  const format = getDateStringFormat(!isThisYear(date));

  return localizedFormat(date, format, languageCode);
};

type Props = { event: ResolvedEvent };

const FutureOccurrenceAlert = ({ children, color = colors.statusRed }: { children: ReactNode; color?: string }) => (
  <Text color={color} fontWeight="bold" whiteSpace="nowrap">
    {"– "}
    &nbsp;{children}
  </Text>
);

const FutureOccurrencesList = ({ event }: Props) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  if (!isRepeatedEventOccurence(event)) return null;

  const futureRepeatedDates = event.series.repeatedEventOccurrences
    ?.filter(isInFuture)
    ?.filter((repeatedDate) => repeatedDate.eventSchedule?.startsAt !== event.eventSchedule?.startsAt); // Not showing current event

  if (!futureRepeatedDates?.length) return null;

  const datesToDisplay = futureRepeatedDates?.slice(0, showAll ? Number.POSITIVE_INFINITY : 3);

  return (
    <Meta label={t("Flere arrangementer i denne serien")} flexDirection="column">
      <List.Root unstyled paddingTop="0.5rem" gap="0.5rem">
        {datesToDisplay?.map((repeatedDate) => (
          <List.Item key={repeatedDate._id}>
            <RepeatedDate repeatedDate={repeatedDate} event={event} />
          </List.Item>
        ))}
      </List.Root>
      {!showAll && futureRepeatedDates.length > datesToDisplay.length && (
        <Link
          marginY=".5em"
          as="button"
          onClick={() => setShowAll(true)}
          display="flex"
          alignItems="center"
          gap=".25em"
        >
          <Plus size="1em" /> {t("Vis alle")}
        </Link>
      )}
    </Meta>
  );
};

const isInFuture = (repeatedDate: ResolvedRepeatedEventOccurrenceSummary): boolean =>
  !!repeatedDate.eventSchedule?.endsAt && new DateHelper(repeatedDate.eventSchedule.endsAt).isAfterNow();

const RepeatedDate = (props: { repeatedDate: ResolvedRepeatedEventOccurrenceSummary; event: Props["event"] }) => {
  const { t, lang } = useTranslation();
  const { eventSchedule } = props.repeatedDate;
  const eventStatusLabel = useEventStatusLabel(props.repeatedDate);

  const timeAltered = props.repeatedDate.scheduleIsCustomized;

  return (
    <ListItem key={props.repeatedDate._id} paddingLeft=".25em">
      <HStack>
        <InternalLink
          _firstLetter={{ textTransform: "uppercase" }}
          href={getPath(props.repeatedDate)}
          whiteSpace="nowrap"
        >
          {eventSchedule?.startsAt && formatFutureDate(lang, eventSchedule.startsAt)}
        </InternalLink>
        {eventStatusLabel && <FutureOccurrenceAlert>{eventStatusLabel.label}</FutureOccurrenceAlert>}
        {timeAltered && (
          <FutureOccurrenceAlert color={colors.statusYellow}>
            {t("Tidspunkt")} {eventTimeString(lang, props.repeatedDate)}
          </FutureOccurrenceAlert>
        )}
      </HStack>
    </ListItem>
  );
};

export default FutureOccurrencesList;
